import type React from 'react';
import type { CSSProperties } from 'react';
import styles from './board-cell.module.scss';
import { START_MARKER_LABEL } from './board-markers';
import { Sparkles, StartRings } from './board-symbols';
import type { BonusKind, CellData } from './board-types';

/**
 * How each premium square is drawn: a pale fill, so the board stays calm, and a soft symbol in its
 * hue. Word premiums are sparkles, two for double word and three for triple. The start square is drawn
 * the same way, as three rings, but it is no premium: it marks where the opening word goes and
 * multiplies nothing.
 */
const BONUS_FILL: Record<BonusKind, string> = {
  '2W': 'var(--wh-word2)',
  '3W': 'var(--wh-word3)',
  '★': 'var(--wh-start)',
};

const BONUS_INK: Record<BonusKind, string> = {
  '2W': 'var(--wh-word2-ink)',
  '3W': 'var(--wh-word3-ink)',
  '★': 'var(--wh-start-ink)',
};

const BONUS_LABEL: Record<Exclude<BonusKind, '★'>, string> = {
  '2W': 'Double word',
  '3W': 'Triple word',
};

const BonusSymbol: React.FC<{ bonus: BonusKind }> = ({ bonus }) =>
  bonus === '★' ? <StartRings /> : <Sparkles count={bonus === '3W' ? 3 : 2} />;

type BonusCellHighlight = 'valid' | 'invalid' | null;

interface BoardCellProps {
  cell: CellData;
  cellSize: number;
  showBonus: boolean;
  covered: boolean;
  highlight: BonusCellHighlight;
}

export const BoardCell: React.FC<BoardCellProps> = ({ cell, cellSize, showBonus, covered, highlight }) => {
  if (cell.hole) {
    return (
      <div aria-hidden="true" className={styles.hole} style={{ '--cell-size': `${cellSize}px` } as CSSProperties} />
    );
  }

  const showB = cell.bonus !== null && showBonus && !covered;

  let overlayColor: string | undefined;
  if (highlight === 'valid') overlayColor = 'color-mix(in oklch, var(--wh-teal) 40%, transparent)';
  else if (highlight === 'invalid') overlayColor = 'color-mix(in oklch, var(--wh-coral) 40%, transparent)';

  const ariaLabel =
    cell.bonus === '★' ? START_MARKER_LABEL : cell.bonus ? `${BONUS_LABEL[cell.bonus]} bonus` : undefined;
  const bgColor = showB && cell.bonus ? BONUS_FILL[cell.bonus] : 'var(--wh-paper)';
  const inkColor = showB && cell.bonus ? BONUS_INK[cell.bonus] : 'transparent';

  return (
    <div
      role="gridcell"
      aria-rowindex={cell.row + 1}
      aria-colindex={cell.col + 1}
      aria-label={ariaLabel}
      className={showB ? `${styles.cell} ${styles.premium}` : styles.cell}
      style={
        {
          '--cell-size': `${cellSize}px`,
          '--cell-bg': bgColor,
          '--cell-color': inkColor,
        } as CSSProperties
      }
    >
      {showB && cell.bonus && <BonusSymbol bonus={cell.bonus} />}
      {overlayColor && (
        <div aria-hidden="true" className={styles.overlay} style={{ '--overlay-bg': overlayColor } as CSSProperties} />
      )}
    </div>
  );
};
